// Web-runtime API impl. Calls the Python API directly over HTTP.
//
// In dev, Vite's proxy in vite.config.ts routes /api/tools/* to the API and
// /api/gateway/* to the LLM gateway. In production nginx does the same.
//
// Every tool is one POST to /api/tools/<name> carrying the signed-in user's
// bearer token. The tool names here are exactly what the backend registers;
// the registry rejects unknown names and unknown kwargs, so nothing is guessed.

import type { Api, IpcResult, Session } from './types'

export const runtime = 'web' as const

// ---- transport ----

async function tool<T>(name: string, payload: Record<string, unknown> = {}): Promise<T> {
  const session = readSession()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (session?.token) headers.Authorization = `Bearer ${session.token}`
  const r = await fetch(`/api/tools/${name}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  })
  const body = await r.json().catch(() => null)
  if (!r.ok) throw new Error((body as { error?: string } | null)?.error || `${name} returned ${r.status}`)
  // Every tool answers { data }. Anything else (a proxy serving HTML, an empty body) is a failure
  // the page must show, not an empty result it renders as zeros.
  if (!body || typeof body !== 'object' || !('data' in body)) throw new Error(`${name} returned no data`)
  return (body as { data?: T }).data as T
}

// Wrap a tool call into the IpcResult envelope. tool() returns the backend's
// `data` only, so we re-wrap here. A tool that answers { error } is a failure.
async function t<T>(name: string, payload: Record<string, unknown> = {}): Promise<IpcResult<T>> {
  try {
    const data = await tool<T & { error?: string }>(name, payload)
    if (data && typeof data === "object" && "error" in data && data.error) {
      return { ok: false, error: data.error }
    }
    return { ok: true, ...data }
  } catch (err) { return { ok: false, error: (err as Error).message } }
}

// ---- auth ----

// lib/session.ts owns the one session read so nothing keeps its own copy of the key.
import { readSession as readStoredSession, writeSession as writeStoredSession } from '../lib/session'

const readSession = (): Session | null => readStoredSession() as Session | null
const writeSession = (s: Session | null): void => writeStoredSession(s)

export const api: Api = {
  auth: {
    async status() {
      try {
        const r = await tool('auth_status', {})
        const data = r as { available?: boolean; userCount?: number }
        return { available: true, userCount: data.userCount ?? 0 }
      } catch {
        return { available: false, userCount: 0 }
      }
    },
    async signup(email, pw, inviteCode): Promise<IpcResult<{ user: { email: string; role: 'admin' | 'user'; token: string } }>> {
      try {
        const r = await tool('signup', { email, pw, invite_code: inviteCode ?? '' })
        const data = r as { user?: { id?: number; email: string; role: 'admin' | 'user'; sections?: string[] }; token?: string }
        if (!data.user || !data.token) return { ok: false, error: 'Signup failed' }
        // Default to 'user' rather than 'admin' (a missing role must never promote), and keep
        // `sections`, which is what the rail filters on.
        writeSession({
          email: data.user.email ?? email,
          role: data.user.role ?? 'user',
          token: data.token,
          ...(data.user.id != null ? { id: data.user.id } : {}),
          ...(Array.isArray(data.user.sections) ? { sections: data.user.sections } : {}),
        })
        return { ok: true, user: { ...data.user, token: data.token } }
      } catch (err) {
        return { ok: false, error: (err as Error).message }
      }
    },
    async login(email, pw, remember): Promise<IpcResult<{ user: Session }>> {
      try {
        const r = await tool('login', { email, pw, remember })
        const data = r as { user?: { id?: number; email: string; role: 'admin' | 'user'; sections?: string[] }; token?: string }
        if (!data.user || !data.token) return { ok: false, error: 'Login failed' }
        // role defaults to 'user', NOT 'admin': a login response missing the role field must
        // demote, never promote. sections is persisted because whoAmI() reads this stored object
        // rather than the server, so it is what the rail filters on.
        writeSession({
          email: data.user.email ?? email,
          role: data.user.role ?? 'user',
          token: data.token,
          ...(data.user.id != null ? { id: data.user.id } : {}),
          ...(Array.isArray(data.user.sections) ? { sections: data.user.sections } : {}),
        })
        return { ok: true, user: { ...data.user, token: data.token } as Session }
      } catch (err) {
        return { ok: false, error: (err as Error).message }
      }
    },
    whoAmI(): Promise<Session | null> { return Promise.resolve(readSession()) },
    async logout() {
      const session = readSession()
      // nginx forwards /api/auth/ so the token is revoked on the server, not only forgotten here.
      if (session?.token) await fetch('/api/auth/logout', { method: 'POST', headers: { Authorization: `Bearer ${session.token}` } }).catch(() => undefined)
      writeSession(null)
      return { ok: true as const }
    },
    async requestReset(email, newPw, code): Promise<IpcResult<unknown>> {
      try {
        const r = await tool('request_reset', { email, new_pw: newPw, code })
        return { ok: true, ...(r as Record<string, unknown>) }
      } catch (err) {
        return { ok: false as const, error: (err as Error).message }
      }
    }
  },

  followups: {
    dueToday: (step = 0, sender = '', limit = 0, force = false) => t('fup_due_today', { step, sender, limit, force }),
    senderRoster: () => t('sender_roster'),
    // These three run the engine's own scripts on the box. Once the job runner is deployed they
    // answer with a job envelope; until then they may still answer with the process output.
    refreshDue: () => t('fup_refresh_due'),
    dryRun: (types = '1,2,3,4,5') => t('fup_dry_run', { types }),
    draft: (types = '1,2,3,4,5', confirm = false) => t('fup_draft', { types, confirm }),
  },

  cold: {
    // Reads all Airtable records, so it is slow on a cold cache and cached for 5 minutes
    // server-side. `force` is what the Refresh button sends.
    queue: (limit = 0, sender = '', readyOnly = false, force = false) =>
      t('cold_queue', { limit, sender, ready_only: readyOnly, force }),
    templates: () => t('cold_templates'),
    dryRun: (count = 0, sender = '', arm = 'evidence') => t('cold_dry_run', { count, sender, arm }),
    assignSenders: (count = 0, sender = '', confirm = false) =>
      t('cold_assign_senders', { count, sender, confirm }),
    draft: (confirm = false) => t('cold_draft', { confirm }),
  },

  admin: {
    generateInvite: () => t('generate_invite_code'),
    listInvites: () => t('list_invite_codes'),
    revokeInvite: (code) => t('revoke_invite_code', { code }),
    listUsers: () => t('list_users'),
    setUserSections: (userId, sectionKeys) => t('set_user_sections', { user_id: userId, sections: sectionKeys }),
    // snake_case: the tool signature is generate_reset_code(target_email) and registry.call
    // rejects unknown kwargs.
    generateResetCode: (targetEmail) => t('generate_reset_code', { target_email: targetEmail }),
    listResetCodes: () => t('list_reset_codes'),
    revokeResetCode: (code) => t('revoke_reset_code', { code })
  },

  log: {
    async post(entry) {
      try {
        await fetch('/api/gateway/logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(entry),
        })
      } catch { /* don't crash the page because logging failed */ }
    },
  },

  home: {
    summary: () => t('home_summary'),
  },

  replies: {
    board: (waitingOnly = false, limit = 0) => t('replies_board', { waiting_only: waitingOnly, limit }),
    thread: (recordId) => t('replies_thread', { record_id: recordId }),
  },

  bookings: {
    board: () => t('bookings_board'),
    sync: (confirm = false) => t('bookings_sync', { confirm }),
  },

  audits: {
    board: () => t('audits_board'),
    detail: (slug) => t('audit_detail', { slug }),
    build: (slug, confirm = false) => t('audit_build', { slug, confirm }),
    verify: (slug) => t('audit_verify', { slug }),
    publish: (slug, confirm = false) => t('audit_publish', { slug, confirm }),
    pdf: (slug) => t('audit_pdf', { slug }),
  },

  jobs: {
    status: (jobId) => t('engine_job_status', { job_id: jobId }),
    log: (jobId, offset = 0, maxBytes = 65536) => t('engine_job_log', { job_id: jobId, offset, max_bytes: maxBytes }),
    list: (kind, key, limit) => t('engine_jobs_list', {
      ...(kind ? { kind } : {}),
      ...(key ? { key } : {}),
      ...(limit ? { limit } : {}),
    }),
    kill: (jobId, confirm = false) => t('engine_job_kill', { job_id: jobId, confirm }),
  },

  definitions: () => t('metric_definitions'),
}
